import React from "react";
import { useParams } from "react-router-dom";
import { fetchCoachingVideoUrl, postCoachingFinished } from "./api";

class CoachingVideoPageInner extends React.Component {
  constructor(props) {
    super(props);
    this.videoRef = React.createRef();
    this.maxWatchedTime = 0;
    this._mounted = false;
    this.state = {
      videoUrl: "",
      isLoading: true,
      isPostingComplete: false,
      isCompletePosted: false,
      currentTime: 0,
      duration: 0,
      isPlaying: false
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.loadVideoUrl();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.token !== this.props.token) {
      this.loadVideoUrl();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  loadVideoUrl = async () => {
    const { token } = this.props;
    this.setState({
      isLoading: true,
      isPostingComplete: false,
      isCompletePosted: false,
      currentTime: 0,
      duration: 0,
      isPlaying: false
    });
    this.maxWatchedTime = 0;

    const url = await fetchCoachingVideoUrl(token);
    if (!this._mounted) {
      return;
    }

    this.setState({
      videoUrl: url,
      isLoading: false
    });
  };

  getProgressPercent() {
    const { currentTime, duration } = this.state;
    if (!duration) {
      return 0;
    }
    return Math.min(100, Math.round((currentTime / duration) * 100));
  }

  handleTimeUpdate = () => {
    const player = this.videoRef.current;
    if (!player) {
      return;
    }

    const current = player.currentTime || 0;
    const allowedTime = this.maxWatchedTime;

    // Native controls can occasionally bypass onSeeking after fullscreen transitions.
    // Re-check every timeupdate and snap back if the user jumped ahead.
    if (current > allowedTime + 0.35) {
      player.currentTime = allowedTime;
      this.setState({ currentTime: allowedTime });
      return;
    }

    this.maxWatchedTime = Math.max(this.maxWatchedTime, current);
    this.setState({ currentTime: current });
  };

  handleLoadedMetadata = () => {
    const total = this.videoRef.current?.duration || 0;
    this.setState({ duration: total });
  };

  handleEnded = () => {
    this.setState({
      isPlaying: false,
      currentTime: this.videoRef.current?.duration || 0
    });
  };

  handleVideoPlay = () => {
    this.setState({ isPlaying: true });
  };

  handleVideoPause = () => {
    this.setState({ isPlaying: false });
  };

  handlePlay = async () => {
    if (!this.videoRef.current) {
      return;
    }

    try {
      await this.videoRef.current.play();
    } catch (_error) {
      this.setState({ isPlaying: false });
    }
  };

  handlePause = () => {
    if (!this.videoRef.current) {
      return;
    }

    this.videoRef.current.pause();
  };

  handleFinish = async () => {
    const { isCompletePosted } = this.state;
    const { token } = this.props;
    if (isCompletePosted) {
      return;
    }

    this.setState({ isPostingComplete: true });
    await postCoachingFinished(token);

    if (!this._mounted) {
      return;
    }

    this.videoRef.current?.pause();

    this.setState({
      isCompletePosted: true,
      isPostingComplete: false
    });
  };

  handleSeeking = () => {
    const player = this.videoRef.current;
    if (!player) {
      return;
    }

    const allowedTime = this.maxWatchedTime;
    if (player.currentTime > allowedTime + 0.2) {
      player.currentTime = allowedTime;
    }
  };

  handleSeeked = () => {
    const player = this.videoRef.current;
    if (!player) {
      return;
    }

    const allowedTime = this.maxWatchedTime;
    if (player.currentTime > allowedTime + 0.2) {
      player.currentTime = allowedTime;
      this.setState({ currentTime: allowedTime });
    }
  };

  render() {
    const { token } = this.props;
    const { videoUrl, isLoading, isPostingComplete, isCompletePosted, currentTime, duration, isPlaying } =
      this.state;
    const progressPercent = this.getProgressPercent();
    const isFinishEnabled = progressPercent >= 100 && !isPostingComplete && !isCompletePosted;
    const playButtonLabel = !isPlaying && currentTime > 0 ? "Resume" : "Start";

    return (
      <main className="page">
        <section className="card">
          <img src="/logo_white_text.svg" alt="Sally logo" className="brand-logo" />
          {isCompletePosted ? (
            <>
              <p className="card-intro">Your coaching session is complete</p>
              <p className="card-intro">Thank You</p>
            </>
          ) : (
            <p className="card-intro">We kindly request you review the weekly coaching video</p>
          )}
          {/* <p className="token">Session token: {token}</p> */}

          {isLoading ? (
            <p className="status">Loading video...</p>
          ) : isCompletePosted ? null : (
            <>
              <video
                ref={this.videoRef}
                src={videoUrl}
                className="player"
                controls
                playsInline
                onPlay={this.handleVideoPlay}
                onPause={this.handleVideoPause}
                onTimeUpdate={this.handleTimeUpdate}
                onLoadedMetadata={this.handleLoadedMetadata}
                onEnded={this.handleEnded}
                onSeeking={this.handleSeeking}
                onSeeked={this.handleSeeked}
              />
              <div className="controls">
                <button className="button" onClick={this.handlePlay} disabled={isPlaying}>
                  {playButtonLabel}
                </button>
                <button className="button" onClick={this.handlePause} disabled={!isPlaying}>
                  Pause
                </button>
                <button className="button finish" onClick={this.handleFinish} disabled={!isFinishEnabled}>
                  Finish
                </button>
              </div>
              <div className="meta">
                <p>Progress: {progressPercent}%</p>
                <p>
                  Position: {Math.floor(currentTime)}s / {Math.floor(duration)}s
                </p>
              </div>
            </>
          )}

          {isPostingComplete && <p className="status">Saving completion...</p>}
        </section>
      </main>
    );
  }
}

function CoachingVideoPage() {
  const { token } = useParams();
  return <CoachingVideoPageInner token={token} />;
}

export default CoachingVideoPage;
